import classNames from 'classnames/bind';
import React from 'react';

import { IconVariant, svgByIconVariant } from './Icon.data';
import styles from './Icon.module.scss';

/** Properties interface of the Icon component. */
export interface IconProps {
  /** Determines if the icon should be read by screen readers. */
  ariaHidden?: boolean;

  /** Custom classname of the Icon component. */
  className?: string;

  /** Style variant of the Icon component. */
  variant: IconVariant;

  /** Aria role for icon element, img */
  role?: React.AriaRole;

  /** Screen readers accessibility label */
  ariaLabel?: string;
}

/**
 * Classnames binding for the Icon component.
 * @see [Classnames Usage](https://github.com/JedWatson/classnames#alternate-bind-version-for-css-modules)
 */
const cx = classNames.bind(styles);

/**
 * A Sitecore-ready implementation of the Icon component.
 *
 * @see [Orchid Storybook](https://upmchporchid.azurewebsites.net/storybook/?path=/docs/docs-icons--page) for design references
 * @see [Orchid Design System](https://upmchporchid.azurewebsites.net/icons) for usage details
 *
 * @example
 * ```tsx
 * <Icon variant="app" />
 * ```
 *
 * @param props - Icon properties.
 * @returns A JSX element.
 */
const Icon: React.FC<IconProps> = (props) => {
  const { ariaHidden, className, variant, role, ariaLabel } = props;

  const classes = cx({ icon: true, [variant as IconVariant]: true }, className);
  const svg = svgByIconVariant[variant];

  return variant === 'none' ? null : (
    <div aria-hidden={ariaHidden} aria-label={ariaLabel} role={role} className={classes} data-testid="icon">
      {svg}
    </div>
  );
};

export default Icon;
